import type { ReactNode } from 'react';

import { isNonFormPdfRecordType, type FormPdfRecordType, type NonFormPdfRecordType } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { usePermissions } from '@/hooks/use-permissions';
import { openExportTab } from '@/utils/export-tab';

import DownloadPdfIcon from './icons/download-pdf';
import RefreshIcon from './icons/refresh';

// Shared by every non-form drawer's onLegacyPrint handler so the browser-print
// page setup stays identical across them. Owned by the fallback mechanism, not
// by each drawer, so a styling change is one edit.
export const RECORD_LEGACY_PRINT_PAGE_STYLE = `
  @page { margin: 30px }
  @media print {
    body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
`;

type SingleRecordPdfControlsBaseProps = {
  recordId: string;
  /** The persisted document URL, when the record already has a generated PDF. */
  documentUrl: string | null | undefined;
};

// Discriminated on record type: non-form records (Issues, Checks) fall back to
// browser print when enableRegisterPdfExport is off, so onLegacyPrint is
// required for them. Form records always use the server export and must not
// pass it. The compiler enforces the pairing, so a new non-form drawer can't
// silently ship without the fallback — the regression this control had before.
type SingleRecordPdfControlsProps = SingleRecordPdfControlsBaseProps &
  ({ recordType: NonFormPdfRecordType; onLegacyPrint: () => void } | { recordType: FormPdfRecordType; onLegacyPrint?: never });

function IconButtonWithTooltip({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" type="button" aria-label={label} onClick={onClick} className="flex items-center gap-2">
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
            <TooltipArrow className="fill-default-text" />
            {label}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Download / regenerate controls for a single-record PDF. The Download button
 * serves the persisted document when present and, when it's missing,
 * (re)generates through the common export service in a dedicated progress tab so
 * the user can keep working here. Superusers get an extra control that forces a
 * regeneration.
 */
export function SingleRecordPdfControls({ recordType, recordId, documentUrl, onLegacyPrint }: SingleRecordPdfControlsProps) {
  const isRegisterPdfEnabled = useFeatureFlag('enableRegisterPdfExport');
  const { isSuperUser } = usePermissions();

  // Non-form records (Issues, Checks) need the register-export flag on top —
  // matching the API's per-type gate. Flag-off they fall back to browser print.
  const isNonForm = isNonFormPdfRecordType(recordType);
  const isServerExportEnabled = !isNonForm || isRegisterPdfEnabled;

  const regenerate = () => {
    openExportTab({ kind: 'pdf', request: { recordType, recordId } });
  };

  const handleDownload = () => {
    if (documentUrl) {
      window.open(documentUrl, '_blank');
      return;
    }
    if (isServerExportEnabled) {
      regenerate();
    } else {
      onLegacyPrint?.();
    }
  };

  return (
    <>
      <IconButtonWithTooltip label="Download PDF" onClick={handleDownload}>
        <DownloadPdfIcon className="h-4 w-4" />
      </IconButtonWithTooltip>
      {/* Non-form records never persist a document, so Download already
          regenerates — a separate Regenerate control would be a duplicate. */}
      {isServerExportEnabled && isSuperUser && !isNonForm && (
        <IconButtonWithTooltip label="Regenerate PDF" onClick={regenerate}>
          <RefreshIcon size={20} />
        </IconButtonWithTooltip>
      )}
    </>
  );
}
